import { defineStore } from "pinia"
import { request } from "~/api/core/request"
import { Logger } from "~/helpers/logger"
import { mapById } from "~/helpers"
import { RANGE_DATE_LIST } from "~/helpers/date-range.helper"
import { DateModel } from "~/model"
import { Api } from "~/api/Api"

const configApi = {
    BASE: import.meta.env.VITE_LARAVEL + "/",
}

export function baseCrudFactory(config) {
    const {
        title,
        endpoint,
        primaryKey = "id",
        modules = [],
        model,
        startDateKey,
        endDateKey,
        state = {},
        filters = {},
        getters = {},
        actions = {},
    } = config

    return defineStore(`${title}`, {
        state: () => ({
            items: [],
            formItem: {},
            formItemCache: false,
            currentItemId: null,
            model: model,
            currentPeriod: {
                label: null,
                startDate: null,
                endDate: null,
            },
            ...state,
        }),
        getters: {
            config() {
                return config
            },
            name() {
                return title
            },
            api() {
                return Api
            },
            isNewFormItem() {
                return (
                    !this.formItem[primaryKey] ||
                    this.formItem[primaryKey] === 0
                )
            },
            currentPeriodFormated() {
                return {
                    startDate: DateModel.formatApi(
                        this.currentPeriod.startDate,
                    ),
                    endDate: DateModel.formatApi(this.currentPeriod.endDate),
                }
            },
            currentItem() {
                return this.itemById[this.currentItemId] || false
            },
            itemsByDate() {
                if (
                    !this.currentPeriod.startDate ||
                    !this.currentPeriod.endDate ||
                    !startDateKey ||
                    !endDateKey
                ) {
                    return []
                }

                return (
                    this.items?.filter((item) => {
                        if (!item[startDateKey] || !item[endDateKey])
                            Logger.error(
                                "store",
                                this.config.title,
                                "itemsByDate",
                                "startDateKey or endDateKey is missing",
                            )

                        return DateModel.isInterval(
                            item[startDateKey],
                            item[endDateKey],
                            this.currentPeriod.startDate,
                            this.currentPeriod.endDate,
                        )
                    }) || []
                ).sort(
                    (a, b) =>
                        new Date(a[startDateKey]) - new Date(b[startDateKey]),
                )
            },
            filterByType(): Record<string, any[]> {
                if (!filters) return {}

                const acc: Record<string, any[]> = Object.keys(filters).reduce(
                    (o, key) => {
                        o[key] = []
                        return o
                    },
                    {} as Record<string, any[]>,
                )

                this.itemsByDate?.forEach((item) => {
                    Object.entries(filters).forEach(([key, predicateFn]) => {
                        if (predicateFn(item)) {
                            acc[key].push(item)
                        }
                    })
                })

                return acc
            },
            itemById() {
                return mapById(this.items, primaryKey) || {}
            },
            dateKey() {
                return {
                    startDate: startDateKey,
                    endDate: endDateKey,
                }
            },
            resourceName() {
                return title || "item"
            },
            ...getters,
        },
        actions: {
            async fetchAll(params) {
                const response = await request(configApi, {
                    method: "GET",
                    url: endpoint,
                    query: params,
                })
                this.setItems(response)
                return response
            },

            async fetchOne(id) {
                try {
                    await this.ready
                    this.currentItemId = id
                    const response = await request(configApi, {
                        method: "GET",
                        url: `${endpoint}/${parseInt(id)}`,
                    })
                    this.setItems(response, false)
                } catch (error) {
                    this.removeStateItem(id)
                    throw new Error(error)
                }
            },
            async create(formItem = null) {
                formItem = formItem || this.formItem
                Object.keys(formItem).forEach((key) => {
                    if (!formItem[key]) {
                        delete formItem[key]
                    }
                })
                const response = await request(configApi, {
                    method: "POST",
                    url: endpoint,
                    body: formItem,
                })
                this.setItems(response, false)
                return response[primaryKey]
            },

            async update(formItem = null) {
                formItem = formItem || this.formItem
                Object.keys(formItem).forEach((key) => {
                    if (formItem[key] === "") {
                        delete formItem[key]
                    }
                })
                if (!formItem[primaryKey]) {
                    throw new Error("Primary key is required for update")
                }
                const response = await request(configApi, {
                    method: "PUT",
                    url: `${endpoint}/${formItem[primaryKey]}`,
                    body: formItem,
                })
                this.setItems(response, false)
                return response[primaryKey]
            },

            async delete(id) {
                await request(configApi, {
                    method: "DELETE",
                    url: `${endpoint}/${id}`,
                })
                this.removeStateItem(id)
            },

            removeStateItem(id) {
                Logger.info("store", this.config.title, "removeStateItem", id)
                this.items = this.items.filter((item) => {
                    return item[primaryKey] !== parseInt(id)
                })
            },

            setItems(itemOrItems: Array<Object> | Object, removeItem = true) {
                const itemsArrayBase = Array.isArray(itemOrItems)
                    ? itemOrItems
                    : [itemOrItems]

                let itemsArray = itemsArrayBase
                if (config.model) {
                    const ModelClass = config.model
                    itemsArray = itemsArrayBase.map((item) =>
                        item instanceof ModelClass
                            ? item
                            : ModelClass.fromData(item),
                    )
                }

                const compteur = {
                    add: [],
                    update: [],
                    remove: [],
                    useModel: !!config.model,
                }

                const newItemIds = new Set(
                    itemsArray.map((item) => item[primaryKey]),
                )
                const hasPeriod =
                    this.currentPeriod.startDate && this.currentPeriod.endDate

                if (removeItem && hasPeriod) {
                    const currentItemsByDate = new Set(this.itemsByDate)
                    this.items = this.items.filter((item) => {
                        const isInItemsByDate = currentItemsByDate.has(item)
                        const isInNewItems = newItemIds.has(item[primaryKey])

                        const shouldKeep = !isInItemsByDate || isInNewItems

                        if (!shouldKeep) {
                            compteur.remove.push(item)
                        }

                        return shouldKeep
                    })
                }

                const itemsMap = new Map(
                    this.items.map((el) => [el[primaryKey], el]),
                )
                itemsArray.forEach((item) => {
                    if (itemsMap.has(item[primaryKey])) {
                        compteur.update.push(item)
                        Object.assign(itemsMap.get(item[primaryKey]), item)
                    } else {
                        compteur.add.push(item)
                        this.items.push(item)
                        itemsMap.set(item[primaryKey], item)
                    }
                })

                Logger.info("store", this.config.title, compteur)

                for (const module in modules) {
                    const key = module.toUpperCase()
                    const relatedItems = itemsArray.flatMap((item) => {
                        if (item[key]) {
                            return Array.isArray(item[key])
                                ? item[key]
                                : [item[key]]
                        }
                        return []
                    })
                    if (relatedItems.length === 0) continue

                    const moduleStore = modules[module]()
                    moduleStore.setItems(relatedItems, removeItem)
                }
            },
            async initForm(id) {
                if (id) {
                    this.currentItemId = id
                    this.formItem = this.currentItem
                    await this.fetchOne(id)
                    this.formItem = this.currentItem
                } else {
                    if (this.formItemCache) this.formItemCache = false
                    else {
                        this.formItem = {}
                    }
                }
            },
            closeForm() {
                this.formItem = {}
                this.formItemCache = false
            },
            // Recalcule startDate/endDate depuis RANGE_DATE_LIST apres rehydrate IDB :
            // les dates persistees sont figees a la session precedente et deviennent
            // obsoletes au reload (ex: "7 derniers jours" pointe sur une plage passee).
            hydratePeriod() {
                const current = this.currentPeriod
                if (!current?.value || current.value === "custom") return
                const fresh = RANGE_DATE_LIST.find(
                    (r) => r.value === current.value,
                )
                if (!fresh) return
                this.currentPeriod = { ...fresh }
            },
            ...actions,
        },
    })
}
